import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, MapPin } from "lucide-react";
import { useEffect, useMemo } from "react";
import { getRelatedCaseStudies } from "@/data/caseStudies";
import { normaliseSchemaDate } from "@/lib/schemaDate";

/**
 * CaseStudyLayout — reusable layout for /case-studies/:slug pages.
 * Same aesthetic as ArticleLayout, but tuned for the case-study body:
 * hero with client + location, metrics strip, and a stronger CTA to
 * signup at the bottom (case studies are heavy-intent surfaces).
 *
 * Schema injected:
 *   - Article schema (BlogPosting-shape but marked as `Article` for
 *     LLMs that treat case studies as commercial content).
 *   - BreadcrumbList (Home > Case studies > <Title>).
 */
export default function CaseStudyLayout({ meta, children }) {
  const related = useMemo(() => getRelatedCaseStudies(meta.slug, 3), [meta.slug]);
  const canonicalUrl = `https://facetra.studio/case-studies/${meta.slug}`;

  const ldjson = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": canonicalUrl,
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
    headline: meta.title,
    description: meta.tldr,
    datePublished: normaliseSchemaDate(meta.published),
    dateModified: normaliseSchemaDate(meta.published),
    author: { "@type": "Organization", name: "Facetra", url: "https://facetra.studio/" },
    publisher: {
      "@type": "Organization",
      name: "Facetra",
      url: "https://facetra.studio/",
      logo: { "@type": "ImageObject", url: "https://facetra.studio/logo.png" },
    },
    inLanguage: "en-IN",
    articleSection: "Case study",
    about: [
      { "@type": "Thing", name: meta.discipline },
      { "@type": "Place", name: meta.location },
    ],
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://facetra.studio/" },
      { "@type": "ListItem", position: 2, name: "Case studies", item: "https://facetra.studio/case-studies" },
      { "@type": "ListItem", position: 3, name: meta.title, item: canonicalUrl },
    ],
  };

  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${meta.title} — Facetra Case Study`;

    const attach = (data, key) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.dataset.caseStudy = `${meta.slug}-${key}`;
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    };
    const s1 = attach(ldjson, "article");
    const s2 = attach(breadcrumb, "crumb");

    const link = document.createElement("link");
    link.rel = "canonical";
    link.dataset.caseStudy = meta.slug;
    link.href = canonicalUrl;
    document.head.appendChild(link);

    const desc = document.createElement("meta");
    desc.name = "description";
    desc.dataset.caseStudy = meta.slug;
    desc.content = meta.tldr;
    document.head.appendChild(desc);

    return () => {
      document.title = prevTitle;
      s1.remove();
      s2.remove();
      link.remove();
      desc.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.slug]);

  return (
    <div
      className="min-h-screen bg-[#0B0B0B] text-white relative overflow-hidden"
      data-testid={`case-study-${meta.slug}`}
    >
      <div className="absolute inset-0 radial-gold-glow opacity-30 pointer-events-none" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Top bar */}
      <header className="relative border-b border-white/5 bg-[#0A0A0A]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="case-study-home-link">
            <div className="w-8 h-8 rounded-md bg-gradient-to-br from-[#F3CE5A] to-[#8B6914] flex items-center justify-center shadow-[0_0_18px_rgba(212,175,55,0.35)]">
              <span className="font-display text-black text-sm font-bold">F</span>
            </div>
            <span className="font-display text-lg tracking-tight">Facetra</span>
          </Link>
          <Link
            to="/case-studies"
            data-testid="case-study-back-link"
            className="text-xs text-white/55 hover:text-[#D4AF37] inline-flex items-center gap-1.5 transition"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> All case studies
          </Link>
        </div>
      </header>

      <main className="relative max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
          Case study · {meta.discipline}
        </div>

        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
          {meta.title}
        </h1>

        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-white/45">
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> {meta.client} — {meta.location}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {meta.readMinutes} min read
          </span>
          <span>Published {formatDate(meta.published)}</span>
        </div>

        {/* Metrics strip */}
        {Array.isArray(meta.metrics) && meta.metrics.length > 0 && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {meta.metrics.map((m) => (
              <div
                key={m.label}
                className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/8 to-transparent p-4"
                data-testid={`case-metric-${m.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/45 mb-1.5">
                  {m.label}
                </div>
                <div className="font-display text-xl text-[#F3CE5A]">{m.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* TL;DR — extracted first by every AI answer engine */}
        <div className="mt-8 rounded-xl border border-white/10 bg-[#0E0E0E] p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">TL;DR</div>
          <p className="text-[14px] text-white/85 leading-relaxed">{meta.tldr}</p>
        </div>

        {/* Body */}
        <div className="mt-10 prose-legal">{children}</div>

        {/* Strong CTA */}
        <div className="mt-14 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">Your atelier next?</div>
          <h3 className="font-display text-2xl mb-3">Run your first design through Facetra today</h3>
          <p className="text-sm text-white/70 mb-4">
            Same workflow. Same speed gains. Free credits to try — no card required.
          </p>
          <Link
            to="/signup"
            data-testid="case-study-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-5 py-2.5 hover:bg-[#F3CE5A] transition"
          >
            Get started free →
          </Link>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16 pt-8 border-t border-white/8">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-5">
              Related case studies
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/case-studies/${r.slug}`}
                  data-testid={`related-case-${r.slug}`}
                  className="group rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
                >
                  <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/40 mb-2">
                    {r.discipline}
                  </div>
                  <div className="text-[13px] text-white font-medium leading-snug group-hover:text-[#D4AF37] transition">
                    {r.title}
                  </div>
                  <div className="mt-3 text-[11px] text-white/40 inline-flex items-center gap-1">
                    Read <ArrowRight className="w-3 h-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Footer nav */}
        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Facetra Design Studio Private Limited</span>
          <div className="flex items-center gap-5">
            <Link to="/how-it-works" className="hover:text-white/70 transition">How it works</Link>
            <Link to="/glossary" className="hover:text-white/70 transition">Glossary</Link>
            <Link to="/articles" className="hover:text-white/70 transition">Articles</Link>
            <Link to="/case-studies" className="hover:text-white/70 transition">Case studies</Link>
            <Link to="/contact" className="hover:text-white/70 transition">Contact</Link>
          </div>
        </div>
      </main>

      <style>{`
        .prose-legal h2 { font-family: 'Fraunces', serif; font-size: 1.6rem; letter-spacing: -0.01em; color: #fff; margin-top: 2.5rem; margin-bottom: 0.85rem; }
        .prose-legal h3 { font-size: 1rem; font-weight: 600; color: #D4AF37; margin-top: 1.75rem; margin-bottom: 0.55rem; text-transform: uppercase; letter-spacing: 0.06em; }
        .prose-legal p, .prose-legal li { font-size: 0.95rem; color: rgba(255,255,255,0.72); line-height: 1.75; }
        .prose-legal p { margin-bottom: 0.95rem; }
        .prose-legal ul, .prose-legal ol { padding-left: 1.15rem; margin-bottom: 0.95rem; }
        .prose-legal ul li { list-style: disc; margin-bottom: 0.4rem; }
        .prose-legal ol li { list-style: decimal; margin-bottom: 0.4rem; }
        .prose-legal strong { color: #fff; font-weight: 600; }
        .prose-legal blockquote { border-left: 2px solid rgba(212,175,55,0.4); padding-left: 1rem; margin: 1.25rem 0; color: rgba(255,255,255,0.75); font-style: italic; }
      `}</style>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}
